
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Phone, MessageCircle, Mail } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Guard } from "@/pages/guards/types";
import { getGuardsByTeamId } from "@/pages/guards/services/guardFetchService";

interface ClientGuardsProps {
  clientId: string;
}

export const ClientGuards = ({ clientId }: ClientGuardsProps) => {
  const [guards, setGuards] = useState<Guard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClientGuards = async () => {
      setIsLoading(true);
      try {
        console.log("Fetching guards for client ID:", clientId);
        
        // Step 1: Get teams assigned to this client
        const { data: clientTeams, error: clientTeamsError } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', clientId);

        if (clientTeamsError) {
          console.error('Error fetching client teams:', clientTeamsError);
          throw clientTeamsError;
        }
        
        console.log("Client teams:", clientTeams);
        
        if (!clientTeams || clientTeams.length === 0) {
          console.log("No teams found for this client");
          setGuards([]);
          setIsLoading(false);
          return;
        }

        const teamIds = clientTeams.map(ct => ct.team_id);
        console.log("Team IDs for fetching members:", teamIds);
        
        // Step 2: For each team, get the guards using our reusable function
        const allGuards: Guard[] = [];
        
        // Process each team to collect all guards
        for (const teamId of teamIds) {
          const teamGuards = await getGuardsByTeamId(teamId);
          allGuards.push(...teamGuards);
        }
        
        // Create a map to remove duplicates by ID
        const uniqueGuardsMap = new Map();
        allGuards.forEach(guard => {
          if (!uniqueGuardsMap.has(guard.id)) {
            uniqueGuardsMap.set(guard.id, guard);
          }
        });
        
        // Convert map to array
        const finalGuards = Array.from(uniqueGuardsMap.values()) as Guard[];
        console.log("Final processed guard profiles:", finalGuards);
        
        setGuards(finalGuards);
      } catch (error) {
        console.error('Error fetching client guards:', error);
        setGuards([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (clientId) {
      fetchClientGuards();
    }
  }, [clientId]);

  if (isLoading) {
    return <div className="text-sm text-gray-500">Φόρτωση φυλάκων...</div>;
  }

  if (guards.length === 0) {
    return <div className="text-sm text-gray-500">Δεν βρέθηκαν φύλακες στις ανατιθέμενες ομάδες</div>;
  }

  return (
    <div className="space-y-3 mt-4">
      <h3 className="text-md font-semibold">Φύλακες στις Ομάδες σας:</h3>
      <div className="grid grid-cols-1 gap-3 max-h-[300px] overflow-y-auto pr-2">
        {guards.map(guard => (
          <Card key={guard.id} className="shadow-sm">
            <CardContent className="p-3">
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10">
                  {guard.avatar_url ? (
                    <img src={guard.avatar_url} alt={guard.full_name || "Guard"} />
                  ) : (
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {(guard.first_name?.[0] || "") + (guard.last_name?.[0] || "")}
                    </AvatarFallback>
                  )}
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {guard.full_name || `${guard.first_name || ""} ${guard.last_name || ""}`}
                  </p>
                </div>
                
                {/* Communication icons */}
                <div className="flex space-x-2">
                  {guard.mobile_phone && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={`tel:${guard.mobile_phone}`}
                            className="hover:text-primary-600 transition-colors"
                          >
                            <Phone className="h-5 w-5 text-green-500 hover:text-primary-600 transition-colors" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Call {guard.mobile_phone}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  
                  {guard.mobile_phone && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={`https://wa.me/${guard.mobile_phone?.replace(/\+/g, '')}`}
                            className="hover:text-primary-600 transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <MessageCircle className="h-5 w-5 text-green-600 hover:text-primary-600 transition-colors" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>WhatsApp message to {guard.first_name || guard.full_name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  
                  {guard.email && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={`mailto:${guard.email}`}
                            className="hover:text-primary-600 transition-colors"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Mail className="h-5 w-5 text-blue-600 hover:text-primary-600 transition-colors" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Email {guard.email}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
